<template>
  <div class="app-wrapper" :class="{ 'fullscreen-mode': isFullscreen }">
    <div v-if="isFullscreen" class="fullscreen-background"></div>
    <div id="app" :class="{ 'fullscreen-mode': isFullscreen }">
      <router-view/>
      
      <!-- Уведомление о технических работах -->
      <!-- <MaintenanceNotice :show="showMaintenanceNotice" @close="handleMaintenanceClose" /> -->
      

      <!-- Глобальное уведомление (не показывается на SplashScreen) -->
      <transition name="notification-fade">
        <div v-if="notificationMessage && currentRoute !== 'Splash'" class="notification-overlay">
          <div class="notification-card">
            <div class="notification-content">
              <p class="notification-text">{{ notificationMessage }}</p>
            </div>
            <button
              class="notification-close"
              @click="closeNotification"
              aria-label="Close notification"
            >
              ✕
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useNotification } from './composables/useNotification.js'
import { useProfileData } from './composables/useProfileData.js'
import { useTelegramData } from './composables/useTelegram.js'
import { useHeartbeat } from './composables/useHeartbeat.js'
import { resetScroll } from './utils/scrollUtils.js'
// import MaintenanceNotice from './components/ui/MaintenanceNotice.vue'

export default {
  name: 'App',
  components: {
    // MaintenanceNotice
  },
  setup() {
    const route = useRoute()
    const { notificationMessage, checkForMessages, closeNotification } = useNotification()
    const { startAutoUpdate, stopAutoUpdate } = useProfileData()
    const { userData, isFullscreen } = useTelegramData()
    
    const currentRoute = computed(() => route.name)
    let heartbeatInstance = null
    
    // Состояние для уведомления о технических работах
    const showMaintenanceNotice = ref(false)
    const MAINTENANCE_SHOWN_KEY = 'maintenance_notice_shown'


    // Проверяем сообщения при монтировании и при изменении маршрута (кроме Splash)
    const checkMessages = async () => {
      if (route.name !== 'Splash') {
        await checkForMessages()
      }
    }

    // Показываем уведомление о технических работах один раз за сессию на Dashboard
    const checkMaintenanceNotice = () => {
      const wasShown = sessionStorage.getItem(MAINTENANCE_SHOWN_KEY)
      if (!wasShown && route.name === 'Dashboard') {
        setTimeout(() => {
          showMaintenanceNotice.value = true
        }, 500)
      }
    }
    
    const handleMaintenanceClose = () => {
      showMaintenanceNotice.value = false
      sessionStorage.setItem(MAINTENANCE_SHOWN_KEY, 'true')
    }
    
    // Запускаем загрузку данных профиля и heartbeat при входе пользователя
    watch(() => userData.value?.id, (userId) => {
      if (userId) {
        // Запускаем автоматическое обновление данных профиля каждые 30 секунд
        startAutoUpdate(30000)
        
        // Запускаем heartbeat только если мы не на Splash экране
        if (route.name !== 'Splash') {
          if (!heartbeatInstance) {
            heartbeatInstance = useHeartbeat(userId, 30000)
          } else {
            // Если экземпляр уже есть, просто обновляем userId и перезапускаем
            heartbeatInstance.stopHeartbeat()
            heartbeatInstance.startHeartbeat(userId)
          }

        }
      }
    }, { immediate: true })
    
    // Управляем heartbeat при изменении маршрута
    watch(() => route.name, (newRoute, oldRoute) => {
      if (userData.value?.id) {
        if (newRoute === 'Splash') {
          // Останавливаем heartbeat на Splash экране
          if (heartbeatInstance) {
            heartbeatInstance.stopHeartbeat()
          }
        } else if (oldRoute === 'Splash' || !heartbeatInstance) {
          // Запускаем heartbeat при выходе с Splash или если его еще нет
          if (heartbeatInstance) {
            heartbeatInstance.startHeartbeat(userData.value.id)
          } else {
            heartbeatInstance = useHeartbeat(userData.value.id, 30000)
          }
        }
      }
    })
    
    // Останавливаем heartbeat при размонтировании
    onUnmounted(() => {
      if (heartbeatInstance) {
        heartbeatInstance.stopHeartbeat()
      }
    })

    onUnmounted(() => {
      stopAutoUpdate()
    })

    onMounted(() => {
      checkMessages()
    })

    watch(() => route.name, (newRoute, oldRoute) => {
      // Сбрасываем скролл при каждом изменении маршрута
      if (newRoute !== oldRoute) {
        setTimeout(() => {
          resetScroll()
        }, 0)
      }
      
      if (newRoute !== 'Splash') {
        checkMessages()
      }
      
      // Проверяем показ уведомления при переходе на Dashboard
      if (newRoute === 'Dashboard' && userData.value?.id) {
        checkMaintenanceNotice()
      }
    })

    return {
      notificationMessage,
      currentRoute,
      closeNotification,
      isFullscreen,
      showMaintenanceNotice,
      handleMaintenanceClose
    }
  },
  mounted() {
    // Предотвращаем double-tap zoom
    let lastTouchEnd = 0
    document.addEventListener('touchend', function (event) {
      const now = Date.now()
      if (now - lastTouchEnd <= 300) {
        event.preventDefault()
      }
      lastTouchEnd = now
    }, false)

    // Отключаем жесты масштабирования
    document.addEventListener('gesturestart', function (e) {
      e.preventDefault()
    })

    document.addEventListener('gesturechange', function (e) {
      e.preventDefault()
    })

    document.addEventListener('gestureend', function (e) {
      e.preventDefault()
    })

    // Отключаем колесо мыши для масштабирования (Ctrl + колесо)
    document.addEventListener('wheel', function (e) {
      if (e.ctrlKey) {
        e.preventDefault()
      }
    }, { passive: false })

    // Отключаем горячие клавиши масштабирования
    document.addEventListener('keydown', function (e) {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0')) {
        e.preventDefault()
      }
    })
  }
}
</script>

<style>
.app-wrapper {
  position: relative;
  width: 100%;
  min-height: 100vh;
}

.app-wrapper.fullscreen-mode {
  margin-top: 0;
}

.fullscreen-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 96px;
  background: rgb(6, 36, 33);
  z-index: 0;
  pointer-events: none;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: manipulation;
}

/* Разрешаем выделение текста в инпутах */
input,
textarea,
[contenteditable] {
  -webkit-user-select: text;
  user-select: text;
  touch-action: auto;
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  overflow-y: hidden;
  touch-action: manipulation;
  -ms-touch-action: manipulation;
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  overscroll-behavior: none;
  -webkit-overscroll-behavior: none;
  /* Скрытие scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

/* Скрытие scrollbar для WebKit браузеров (Chrome, Safari, Opera) */
html::-webkit-scrollbar,
body::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

#app {
  width: 100%;
  min-height: 100vh;
  background: rgb(6, 36, 33);
  color: var(--color-text-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  touch-action: manipulation;
  overscroll-behavior: none;
  -webkit-overscroll-behavior: none;
  /* Скрытие scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  position: relative;
  z-index: 1;
}

/* Закругление и стили для fullscreen режима */
#app.fullscreen-mode {
  position: fixed;
  top: 96px;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 32px 32px 0 0;
  overflow: hidden;
  background: #000;
  padding-bottom:100px;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Скрытие scrollbar для всех элементов внутри #app */
#app * {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

#app *::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}


/* Стили для router-link */
a {
  color: inherit;
  text-decoration: none;
}

/* Notification styles */
.notification-fade-enter-active,
.notification-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.notification-fade-enter-from,
.notification-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  padding: var(--spacing-lg);
  pointer-events: none;
}

.notification-card {
  margin-top: 100px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-md) var(--spacing-lg);
  max-width: 480px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
}

.notification-content {
  flex: 1;
}

.notification-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.5;
}

.notification-close {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  font-size: 20px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
  transition: all 0.2s ease;
  padding: 0;
}

.notification-close:hover {
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
}

.notification-close:active {
  transform: scale(0.9);
}

@media (max-width: 480px) {
  .notification-overlay {
    padding: var(--spacing-md);
  }

  .notification-card {
    padding: var(--spacing-sm) var(--spacing-md);
  }
}
</style>